import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CreditPacksResponse,
  LightConvertResponse,
  LightPaymentHistory,
  MarketplacePage,
} from 'src/app/interfaces/signup-page.interface';

/** Clé sessionStorage de la page où revenir après connexion (?returnUrl=). */
const RETURN_URL_KEY = 'post_login_return_url';

/**
 * Comptes LÉGERS (inscrits via une page d'inscription personnalisée) : ils
 * ne voient jamais le BO classique — uniquement la marketplace des pages perso
 * et le chat de ces pages. Ce service centralise :
 *  - la détection (user.is_light_account, lu sur /me ou le cache localStorage),
 *  - la redirection post-connexion (returnUrl interne, sinon /marketplace pour
 *    un compte léger et /chat pour un compte classique),
 *  - les appels marketplace / conversion.
 */
@Injectable({ providedIn: 'root' })
export class LightAccountService {
  private get apiUrl(): string {
    return (window as any).__env.apiUrl;
  }

  constructor(private http: HttpClient) {}

  /** Compte léger ? (utilisateur passé, sinon cache localStorage de /me). */
  isLight(user?: any): boolean {
    const u = user ?? this.cachedUser();
    return !!u?.is_light_account;
  }

  cachedUser(): any {
    try {
      return JSON.parse(localStorage.getItem('currentUser') || 'null');
    } catch {
      return null;
    }
  }

  /**
   * N'accepte que des chemins internes à l'app (« /… », jamais « //… » ni
   * d'URL absolue) : un returnUrl ne doit pas pouvoir renvoyer ailleurs.
   */
  sanitizeReturnUrl(url: string | null | undefined): string | null {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.startsWith('/\\'))
      return null;
    return trimmed;
  }

  rememberReturnUrl(url: string | null | undefined) {
    const safe = this.sanitizeReturnUrl(url);
    if (safe) sessionStorage.setItem(RETURN_URL_KEY, safe);
  }

  /** Page de retour mémorisée, consommée (lue une seule fois). */
  takeReturnUrl(): string | null {
    const url = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    return this.sanitizeReturnUrl(url);
  }

  /**
   * Destination après connexion. Un compte léger ne peut revenir que sur une
   * page perso (/base/…) ou la marketplace ; un compte classique reprend sa
   * page de retour, sinon /chat (comportement historique).
   */
  postLoginUrl(user: any, returnUrl: string | null): string {
    if (this.isLight(user)) {
      if (returnUrl && (returnUrl.startsWith('/base/') || returnUrl.startsWith('/marketplace')))
        return returnUrl;
      return '/marketplace';
    }
    return returnUrl || '/chat';
  }

  /** URL de la page d'inscription personnalisée, avec retour vers la page perso. */
  signupUrl(signupSlug: string, shareKey?: string | null): string {
    const qs = shareKey ? `?from=${encodeURIComponent(shareKey)}` : '';
    return `/inscription/${encodeURIComponent(signupSlug)}${qs}`;
  }

  /** URL de connexion avec retour vers la page courante. */
  loginUrl(returnUrl?: string | null): string {
    const safe = this.sanitizeReturnUrl(returnUrl);
    return safe ? `/login?returnUrl=${encodeURIComponent(safe)}` : '/login';
  }

  // ----- API -----

  getMarketplaceShares(q = '', page = 1): Observable<MarketplacePage> {
    const params: Record<string, string> = { page: String(page) };
    if (q) params['q'] = q;
    return this.http.get<MarketplacePage>(`${this.apiUrl}/marketplace/shares`, { params });
  }

  /** Lance (ou relance : idempotent) la conversion ; nom optionnel à la reprise. */
  convert(companyName: string | null): Observable<LightConvertResponse> {
    return this.http.post<LightConvertResponse>(`${this.apiUrl}/light-account/convert`, {
      company_name: companyName || null,
    });
  }

  /** Packs de NrToks achetables (et taxes du territoire de facturation). */
  getCreditPacks(): Observable<CreditPacksResponse> {
    return this.http.get<CreditPacksResponse>(`${this.apiUrl}/light-account/credit-packs`);
  }

  /** Ouvre un paiement Stripe intégré pour un pack ; renvoie le client_secret à monter. */
  checkoutCreditPack(productId: number, taxRateId: number): Observable<{ client_secret: string }> {
    return this.http.post<{ client_secret: string }>(`${this.apiUrl}/light-account/credit-packs/checkout`, {
      product_id: productId,
      tax_rate_id: taxRateId,
    });
  }

  /** Paiements passés du compte léger (packs réglés + total dépensé). */
  getPaymentHistory(): Observable<LightPaymentHistory> {
    return this.http.get<LightPaymentHistory>(`${this.apiUrl}/light-account/payments`);
  }

  /** Abandon d'une conversion non payée : le compte redevient « léger sans tunnel en cours ». */
  cancelConversion(): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.apiUrl}/light-account/convert/cancel`, {});
  }
}
